import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { routeAfterSignIn } from '../legal/postLoginRoute';
import { prefetchDashboard } from '../utils/dashboardCache';
import { warmInvestigationCaches } from '../utils/warmInvestigationCaches';
import { exitGuestDemo } from '../utils/guestDemo';

/**
 * Partner SSO landing route.
 *
 * The CryptoFlow backend's `/api/v1/auth/sso/launch` verifies the portal
 * handoff token, mints a normal CryptoFlow session JWT, and 303-redirects the
 * browser here with the token in the URL *fragment*:
 *
 *     /sso-callback#session=<jwt>[&case=<case-id>]
 *
 * The fragment (not the query string) is used so the token never reaches a
 * server log / proxy / CDN. This handler reads it, stores it exactly where a
 * password login would (`localStorage['token']` + `localStorage['user']`, see
 * Login.tsx), scrubs it from the URL, and routes into the app.
 */

interface DecodedSession {
  sub?: string;
  username?: string;
  role?: string;
}

function decodeJwtPayload(token: string): DecodedSession | null {
  try {
    const seg = token.split('.')[1];
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

export const SsoCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const raw = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
    const params = new URLSearchParams(raw);
    const sessionToken = params.get('session');
    const caseId = params.get('case');

    if (!sessionToken) {
      navigate('/login?error=sso_failed', { replace: true });
      return;
    }

    localStorage.setItem('token', sessionToken);
    exitGuestDemo();
    void prefetchDashboard({ force: true }).then(() => warmInvestigationCaches());

    const claims = decodeJwtPayload(sessionToken);
    if (claims) {
      localStorage.setItem(
        'user',
        JSON.stringify({ username: claims.username, role: claims.role }),
      );
    }

    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {
      /* non-fatal */
    }

    const dest = caseId ? `/cases/${encodeURIComponent(caseId)}` : '/';

    void routeAfterSignIn(navigate, dest);
  }, [location, navigate]);

  return (
    <div
      className={clsx(
        'flex h-screen w-full items-center justify-center p-4 relative overflow-hidden',
        'bg-brand-page dark:bg-background',
      )}
      data-testid="sso-callback-page"
    >
      <div
        className={clsx(
          'w-full max-w-sm relative z-10 rounded-xl overflow-hidden',
          'bg-card text-card-foreground border border-border',
        )}
        data-testid="sso-callback-card"
      >
        <div className="h-1 w-full bg-primary" data-testid="sso-callback-accent" />
        <div className="flex flex-col items-center gap-4 p-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white dark:bg-brand-ink ring-1 ring-primary/30">
            <img
              src="/brand/cryptoflow-icon-light.svg"
              alt="CryptoFlow"
              className="h-7 w-7 block dark:hidden"
            />
            <img
              src="/brand/cryptoflow-icon-dark.svg"
              alt="CryptoFlow"
              className="h-7 w-7 hidden dark:block"
            />
          </div>
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"
            role="status"
            aria-label="Loading"
          />
          <p className="text-sm font-medium text-center text-foreground">
            Signing you in via Outris portal…
          </p>
        </div>
      </div>
    </div>
  );
};

export default SsoCallback;
